import React from 'react';
import AudioPlayerHeadWindowActions from './AudioPlayerHeadWindowActions';

const MOBILE_PANES = [
  { id: 'player', label: 'Now Playing' },
  { id: 'playlist', label: 'Up Next' },
  { id: 'library', label: 'Browse' },
];

export function useCoarseMobileLayout() {
  const [mobile, setMobile] = React.useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 960px)').matches : false
  );
  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 960px)');
    const apply = () => setMobile(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);
  return mobile;
}

function MobilePaneWindowActions({ show, onMinimize, onClose }) {
  if (!show || (!onMinimize && !onClose)) return null;
  return (
    <div className="audio-player__head-actions audio-player__head-actions--pane">
      <AudioPlayerHeadWindowActions onMinimize={onMinimize} onClose={onClose} />
    </div>
  );
}

/**
 * Three-column studio: player | up next | storage box library.
 * Used in Storage Box modal and Audio Player product page (Storage Box tab).
 */
export default function StorageBoxAudioStudioLayout({
  player,
  playlistPanel,
  library,
  mobileLayout,
  mobilePane,
  onMobilePaneChange,
  trackCount = 0,
  shellClassName = '',
  paneClassPrefix = 'sb-audio-player-modal',
  onMinimize = null,
  onClose = null,
  playerEmbedsWindowActions = true,
}) {
  const shellClass = [
    'sb-audio-player-modal__studio',
    shellClassName,
    mobileLayout ? `${paneClassPrefix}--pane-${mobilePane}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  const renderPaneWindowActions = (paneId) => {
    if (paneId !== 'player') return null;
    if (!mobileLayout) return null;
    if (playerEmbedsWindowActions) return null;
    return (
      <MobilePaneWindowActions
        show={mobilePane === 'player'}
        onMinimize={onMinimize}
        onClose={onClose}
      />
    );
  };

  return (
    <>
      {mobileLayout ? (
        <div className="sb-audio-player-modal__mobile-chrome">
          <div className="sb-audio-player-modal__pane-tabs" role="tablist" aria-label="Audio player sections">
            {MOBILE_PANES.map((pane) => (
              <button
                key={pane.id}
                type="button"
                role="tab"
                aria-selected={mobilePane === pane.id}
                className={`sb-audio-player-modal__pane-tab${
                  mobilePane === pane.id ? ' sb-audio-player-modal__pane-tab--active' : ''
                }`}
                onClick={() => onMobilePaneChange?.(pane.id)}
              >
                {pane.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className={shellClass}>
        <section
          className="sb-audio-player-modal__col sb-audio-player-modal__col--player"
          aria-label="Now playing"
          data-modal-scroll-port
        >
          {!mobileLayout ? (
            <header className="sb-audio-player-modal__col-head">
              <p className="sb-audio-player-modal__col-eyebrow">Now Playing</p>
              {onMinimize || onClose ? (
                <div className="sb-audio-player-modal__col-head-actions">
                  <AudioPlayerHeadWindowActions onMinimize={onMinimize} onClose={onClose} />
                </div>
              ) : null}
            </header>
          ) : null}
          <div className="sb-audio-player-modal__col-body sb-audio-player-modal__col-body--player">
            {renderPaneWindowActions('player')}
            {player}
          </div>
        </section>

        <section
          className="sb-audio-player-modal__col sb-audio-player-modal__col--playlist"
          aria-label="Up next"
          data-modal-scroll-port
        >
          <header className="sb-audio-player-modal__col-head">
            <p className="sb-audio-player-modal__col-eyebrow">Up Next</p>
            <p className="sb-audio-player-modal__col-meta">
              {trackCount} {trackCount === 1 ? 'track' : 'tracks'}
            </p>
          </header>
          <div className="sb-audio-player-modal__col-body sb-audio-player-modal__col-body--playlist">
            {playlistPanel}
          </div>
        </section>

        <section
          className="sb-audio-player-modal__col sb-audio-player-modal__col--library"
          aria-label="Storage Box library"
          data-modal-scroll-port
        >
          <header className="sb-audio-player-modal__col-head">
            <p className="sb-audio-player-modal__col-eyebrow">Storage Box</p>
            <p className="sb-audio-player-modal__col-meta">Add songs to your queue</p>
          </header>
          <div className="sb-audio-player-modal__col-body sb-audio-player-modal__col-body--library">
            {library}
          </div>
        </section>
      </div>
    </>
  );
}
